import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import DashboardHeader from "@/components/DashboardHeader";
import DashboardFooter from "@/components/DashboardFooter";
import { useOptionalMerchant } from "@/lib/useMerchant";
import BackToDashboard from "@/components/BackToDashboard";
import { Truck, Tag, Repeat, Timer, Trash2, Info, MessageCircle, Package } from "lucide-react";

/**
 * Plain-English reference for shipping fees and discount rules — how
 * priority works, when the bot asks for a code, one-use-per-customer,
 * limited-time windows, and what deleting a used rule does. Companion
 * to ShippingInfo.jsx, which links here instead of explaining it inline.
 */

const TOPICS = [
  {
    icon: Truck,
    name: "Shipping fee",
    body: "A flat delivery charge added to every order's total, on every plan. Set it to 0 for free shipping across the board.",
    example: "Example: shipping fee set to ₹49. A customer's ₹450 cart becomes ₹499 at checkout — before any discount is applied.",
  },
  {
    icon: Tag,
    name: "Manual codes vs. automatic offers",
    body: "A manual code is something the customer types in DM, like WELCOME10. An automatic offer applies on its own the moment a cart qualifies — no code needed.",
    example: "Example: \"Free shipping over ₹500\" as an automatic offer — any cart crossing ₹500 gets it with no action from the customer. \"WELCOME10\" as a manual code — the customer has to type it for it to apply.",
  },
  {
    icon: Package,
    name: "Quantity-based discounts (Buy X Get Y% Off)",
    body: "Available on Pro. An automatic offer can require a minimum cart quantity in addition to a minimum order value. When both are set on the same rule, a cart has to clear both conditions together before the discount applies — it's an \"and\", not an \"or\".",
    example: "Example: a rule set to ₹100 off orders over ₹1000, with a minimum quantity of 2. A cart with 3 items totalling ₹600 does not qualify — it clears the quantity condition but not the value condition. A cart with 1 item worth ₹1,200 also does not qualify — it clears the value condition but not the quantity condition. Only a cart with 2+ items and a ₹1,000+ subtotal gets the ₹100 off.",
  },
  {
    icon: Repeat,
    name: "Only one discount per order",
    body: "A single order can never combine two rules. If more than one automatic offer qualifies for a cart, the customer automatically gets whichever one saves them the most money — not whichever you created first.",
    example: "Example: a ₹700 cart qualifies for both \"free shipping over ₹500\" (saves ₹49) and \"15% off over ₹600\" (saves ₹105). The customer gets the 15% off, since it saves more.\n\nIf two active automatic offers would save the customer the exact same amount, only one applies — but which one isn't guaranteed to be consistent. Avoid keeping two active automatic offers that could both trigger on the same cart for an identical discount.",
  },
  {
    icon: MessageCircle,
    name: "When the bot asks for a code",
    body: "The bot only prompts a customer for a manual code when no automatic offer already applies to their cart, and you have at least one active manual code. If an automatic offer already covers them, it won't ask.",
    example: "Example: your only active rule is \"WELCOME10\" (manual). A customer with no qualifying automatic offer will be asked \"Have a discount code?\" before checkout.",
  },
  {
    icon: Repeat,
    name: "Each rule, once per customer, ever",
    body: "Once a customer redeems a rule — automatic or manual — it won't apply to their next order. You don't need to expire or babysit codes to stop repeat use; this is automatic per customer.",
    example: "Example: a customer uses WELCOME10 on their first order. On their second order, typing WELCOME10 again (or otherwise qualifying) won't apply the discount — it's already been used by that customer.",
  },
  {
    icon: Timer,
    name: "Limited-time windows",
    body: "Want an offer to end on its own regardless of redemptions — like \"ends this Sunday at noon\"? Set a start and/or end time when creating the rule. Times you enter are always in your account's timezone. Once the end time passes, the rule stops applying automatically, even for customers who never used it.",
    example: "Example: \"FESTIVE20\" set to end Sunday at 12:00 PM. At 12:01 PM Sunday, it stops applying to new orders — even for a first-time customer who never got the chance to use it.\n\nNote: the \"Tonight\" quick-preset sets the end time to 11:59 PM today rather than 12:00 AM — 12:00 AM is technically the start of the next calendar day, which made the preset look like it was picking the wrong date. 11:59 PM keeps the end time on the day you actually meant.",
  },
  {
    icon: Trash2,
    name: "Deleting a used rule",
    body: "If a rule has already been redeemed by at least one customer, deleting it keeps that past order's history intact behind the scenes. You'll just stop seeing the rule in your list, and its code text frees up for reuse in a new rule.",
    example: "Example: you delete WELCOME10 after 12 customers used it. Those 12 orders still show the discount they got. You can now create a fresh rule also called WELCOME10 if you want.",
  },
];

function TopicCard({ icon: Icon, name, body, example }) {
  return (
    <div className="rounded-3xl border border-ink/10 bg-white p-6">
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 flex-none place-items-center rounded-xl bg-hotpink/10 text-hotpink">
          <Icon size={16} />
        </span>
        <p className="font-display text-xl font-black tracking-tight">{name}</p>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-ink/70">{body}</p>
      {example && (
        <div className="mt-3 flex gap-2.5 rounded-2xl border border-ink/10 bg-ink/[0.02] p-3.5">
          <Info size={14} className="mt-0.5 flex-none text-ink/40" />
          <p className="whitespace-pre-line text-xs leading-relaxed text-ink/60">{example}</p>
        </div>
      )}
    </div>
  );
}

export default function DiscountsGuide() {
  const { me } = useOptionalMerchant();

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: "Shipping Fees & Discounts Guide",
    description: "How flat shipping fees and discount rules work, including how priority is decided when more than one offer applies.",
    publisher: { "@type": "Organization", name: "Checkout DM", url: "https://checkoutdm.com" },
    mainEntityOfPage: "https://checkoutdm.com/discounts-guide",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://checkoutdm.com/" },
      { "@type": "ListItem", position: 2, name: "Shipping Fees & Discounts Guide", item: "https://checkoutdm.com/discounts-guide" },
    ],
  };

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Shipping Fees & Discounts Guide | Checkout DM"
        description="How flat shipping fees and discount rules work, including how priority is decided when more than one offer applies."
        path="/discounts-guide"
        structuredData={[articleSchema, breadcrumbSchema]}
      />
      <DashboardHeader me={me} />

      <main className="mx-auto max-w-4xl px-6 pt-10 md:px-10">
        {me && <BackToDashboard />}
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Help</p>
        <h1 className="mt-2 font-display text-4xl font-black tracking-tighter">
          Shipping fees & discounts, explained.
        </h1>
        <p className="mt-3 max-w-2xl text-ink/70">
          How your flat shipping fee and discount rules work, with worked examples for each —
          including how priority is decided when more than one offer could apply.{" "}
          <Link to="/tabs-guide" className="font-semibold text-hotpink hover:underline">
            Looking for what a dashboard tab means instead? →
          </Link>
        </p>

        <div className="mt-8 space-y-4">
          {TOPICS.map((t) => (
            <TopicCard key={t.name} {...t} />
          ))}
        </div>
      </main>

      <div className="mt-10">
        <DashboardFooter />
      </div>
    </div>
  );
}
